/**
 * Alert Rules Configuration Page
 * Manage alert rules for GPS tracking
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Modal,
  useWindowDimensions,
  Platform,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Surface, useTheme, Appbar } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import gpsTrackingAPI from '../../../src/api/services/gpsTracking';
import AlertRuleForm from '../../../src/components/GPS/AlertRuleForm';
import { Sidebar, SIDEBAR_WIDTH } from '../../../src/components/Sidebar';
import { useUser } from '../../../src/hooks/useUser';
import { useProfile } from '../../../src/hooks/useProfile';
import Logo from '../../../src/components/Logo';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

interface AlertRule {
  id: number;
  name: string;
  alert_type: string;
  is_active: boolean;
  devices_count: number;
  geofences_count: number;
  notification_channels: string[];
  speed_limit?: number;
  offline_threshold?: number;
  cooldown_minutes: number;
  created_at: string;
  updated_at: string;
}

export default function AlertRulesPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isLargeScreen = width > 1024;
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarVisible, setDesktopSidebarVisible] = useState(isLargeScreen);
  
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  
  const { handleLogout, userData } = useUser();
  const { selectedProfile, effectiveProfile, updateProfile, hasOwner, hasDriver } = useProfile(userData?.groups || []);
  
  useEffect(() => {
    if (isLargeScreen) {
      setDesktopSidebarVisible(true);
      setSidebarOpen(false);
    } else {
      setDesktopSidebarVisible(false);
    }
  }, [isLargeScreen, width]);

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      setLoading(true);
      const data = await gpsTrackingAPI.alertRules.listRules();
      setRules(data);
    } catch (error) {
      console.error('Failed to load alert rules:', error);
      Alert.alert('Erro', 'Falha ao carregar regras de alerta');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = () => {
    setEditingRule(null);
    setShowCreateModal(true);
  };

  const handleEditRule = (rule: AlertRule) => {
    setEditingRule(rule);
    setShowCreateModal(true);
  };

  const handleDeleteRule = (rule: AlertRule) => {
    Alert.alert(
      'Confirmar exclusão',
      `Tem certeza que deseja excluir a regra "${rule.name}"?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Excluir',
          style: 'destructive',
          onPress: async () => {
            try {
              await gpsTrackingAPI.alertRules.deleteRule(rule.id);
              await loadRules();
              Alert.alert('Sucesso', 'Regra excluída com sucesso');
            } catch (error) {
              Alert.alert('Erro', 'Falha ao excluir regra');
            }
          },
        },
      ]
    );
  };

  const handleToggleActive = async (rule: AlertRule) => {
    try {
      await gpsTrackingAPI.alertRules.updateRule(rule.id, {
        is_active: !rule.is_active,
      });
      await loadRules();
    } catch (error) {
      Alert.alert('Erro', 'Falha ao atualizar regra');
    }
  };

  const handleSubmitRule = async (data: any) => {
    try {
      if (editingRule) {
        await gpsTrackingAPI.alertRules.updateRule(editingRule.id, data);
        Alert.alert('Sucesso', 'Regra atualizada com sucesso');
      } else {
        await gpsTrackingAPI.alertRules.createRule(data);
        Alert.alert('Sucesso', 'Regra criada com sucesso');
      }
      setShowCreateModal(false);
      await loadRules();
    } catch (error: any) {
      console.error('Failed to save rule:', error);
      const errorMsg = error?.response?.data?.error || 'Falha ao salvar regra';
      Alert.alert('Erro', errorMsg);
    }
  };

  const getAlertTypeLabel = (alertType: string): string => {
    const labels: Record<string, string> = {
      'geofence_enter': 'Entrada em Geocerca',
      'geofence_exit': 'Saída de Geocerca',
      'speed_exceeded': 'Excesso de Velocidade',
      'device_offline': 'Dispositivo Offline',
      'device_online': 'Dispositivo Online',
      'ignition_on': 'Ignição Ligada',
      'ignition_off': 'Ignição Desligada',
      'low_battery': 'Bateria Fraca',
      'sos': 'Alarme SOS',
      'harsh_braking': 'Frenagem Brusca',
      'harsh_acceleration': 'Aceleração Brusca',
      'harsh_cornering': 'Curva Brusca',
      'idle_excessive': 'Inatividade Excessiva',
      'towing_detected': 'Reboque Detectado',
      'device_tamper': 'Violação do Dispositivo',
      'fuel_drop': 'Queda de Combustível',
      'fatigue_driving': 'Fadiga ao Dirigir',
      'maintenance_due': 'Manutenção Devida',
    };
    return labels[alertType] || alertType;
  };

  const getAlertTypeIcon = (alertType: string) => {
    if (alertType.includes('geofence')) return 'location-outline';
    if (alertType.includes('speed')) return 'speedometer-outline';
    if (alertType.includes('offline') || alertType.includes('online')) return 'wifi-outline';
    if (alertType.includes('ignition')) return 'key-outline';
    if (alertType.includes('battery')) return 'battery-half-outline';
    if (alertType.includes('sos')) return 'warning-outline';
    if (alertType.includes('harsh')) return 'alert-circle-outline';
    if (alertType.includes('idle')) return 'time-outline';
    if (alertType.includes('maintenance')) return 'construct-outline';
    return 'notifications-outline';
  };

  const getAlertTypeColor = (alertType: string) => {
    if (alertType.includes('sos')) return '#FF3B30';
    if (alertType.includes('speed') || alertType.includes('harsh')) return '#FF9500';
    if (alertType.includes('offline')) return '#FF9500';
    if (alertType.includes('online')) return '#34C759';
    if (alertType.includes('maintenance')) return '#007AFF';
    return '#8E8E93';
  };

  const renderHeader = () => {
    if (isLargeScreen) return null;
    
    return (
      <Appbar.Header style={{ 
        height: SCREEN_HEIGHT * 0.1, 
        backgroundColor: theme.colors.surface,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1
      }}>
        <Appbar.Action icon="menu" onPress={() => setSidebarOpen(true)} color={theme.colors.onSurface} />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Logo 
            size={Platform.OS === 'web' ? 'medium' : 'mobile'} 
            style={{ maxWidth: Platform.OS === 'web' ? 200 : 250 }} 
          />
        </View>
        <View style={{ width: 48 }} />
      </Appbar.Header>
    );
  };

  return (
    <View style={styles.outerContainer}>
      {/* Desktop Sidebar */}
      {desktopSidebarVisible && (
        <Sidebar
          key={`desktop-sidebar-${width}`}
          isOpen={true}
          onClose={() => {}}
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={true}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasOwner && hasDriver ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
          onRegisterDriver={!hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
        />
      )}

      {/* Main Content */}
      <Surface style={{ flex: 1, backgroundColor: theme.colors.background, ...(desktopSidebarVisible ? { marginLeft: SIDEBAR_WIDTH } : {}) }}>
        {renderHeader()}
        
        <View style={[styles.container, !isLargeScreen && { marginTop: SCREEN_HEIGHT * 0.1 }]}>
          {/* Page Header */}
          <View style={styles.pageHeader}>
            <View style={styles.headerTopRow}>
              <Text style={styles.pageTitle}>Regras de Alerta ({rules.length})</Text>
              <TouchableOpacity
                style={styles.createButton}
                onPress={handleCreateRule}
              >
                <Ionicons name="add-circle" size={20} color="#FFFFFF" />
                <Text style={styles.createButtonText}>Nova Regra</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.switcher}>
              <TouchableOpacity
                style={styles.switchButton}
                onPress={() => router.push('/dashboard/gps/alerts')}
              >
                <Text style={styles.switchButtonText}>Ver alertas</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.switchButton, styles.switchButtonActive]}
                onPress={() => {}}
              >
                <Text style={[styles.switchButtonText, styles.switchButtonTextActive]}>Gerir regras</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Loading state */}
          {loading && (
            <View style={styles.centerContainer}>
              <ActivityIndicator size="large" color="#007AFF" />
            </View>
          )}

          {/* Rules List */}
          {!loading && (
            <ScrollView 
              style={styles.list} 
              contentContainerStyle={rules.length === 0 ? styles.listContentContainer : undefined}
            >
              {rules.length === 0 ? (
                <View style={styles.emptyState}>
                  <Ionicons name="notifications-outline" size={64} color="#C7C7CC" />
                  <Text style={styles.emptyStateTitle}>Nenhuma regra configurada</Text>
                  <Text style={styles.emptyStateText}>
                    Crie sua primeira regra de alerta para receber notificações sobre eventos importantes
                  </Text>
                  <TouchableOpacity
                    style={styles.createButtonEmpty}
                    onPress={handleCreateRule}
                  >
                    <Ionicons name="add-circle-outline" size={20} color="#FFFFFF" />
                    <Text style={styles.createButtonEmptyText}>Criar Regra</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                rules.map((rule) => (
                  <View key={rule.id} style={styles.ruleCard}>
                    <View style={styles.ruleHeader}>
                      <View style={styles.ruleTitleRow}>
                        <Ionicons
                          name={getAlertTypeIcon(rule.alert_type)}
                          size={24}
                          color={getAlertTypeColor(rule.alert_type)}
                        />
                        <View style={styles.ruleInfo}>
                          <Text style={styles.ruleName}>{rule.name}</Text>
                          <Text style={styles.ruleType}>
                            {getAlertTypeLabel(rule.alert_type)}
                          </Text>
                        </View>
                        {!rule.is_active && (
                          <View style={styles.inactiveBadge}>
                            <Text style={styles.inactiveBadgeText}>Inativa</Text>
                          </View>
                        )}
                      </View>

                      <View style={styles.ruleActions}>
                        <TouchableOpacity
                          onPress={() => handleToggleActive(rule)}
                          style={styles.actionButton}
                        >
                          <Ionicons
                            name={rule.is_active ? 'pause-circle-outline' : 'play-circle-outline'}
                            size={20}
                            color={rule.is_active ? '#FF9500' : '#34C759'}
                          />
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => handleEditRule(rule)}
                          style={styles.actionButton}
                        >
                          <Ionicons name="create-outline" size={20} color="#007AFF" />
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => handleDeleteRule(rule)}
                          style={styles.actionButton}
                        >
                          <Ionicons name="trash-outline" size={20} color="#FF3B30" />
                        </TouchableOpacity>
                      </View>
                    </View>

                    <View style={styles.ruleDetails}>
                      <View style={styles.ruleDetailItem}>
                        <Ionicons name="car-outline" size={14} color="#8E8E93" />
                        <Text style={styles.ruleDetailText}>
                          {rule.devices_count === 0 ? 'Todos os veículos' : `${rule.devices_count} veículo(s)`}
                        </Text>
                      </View>
                      {rule.alert_type.includes('geofence') && (
                        <View style={styles.ruleDetailItem}>
                          <Ionicons name="shapes-outline" size={14} color="#8E8E93" />
                          <Text style={styles.ruleDetailText}>
                            {rule.geofences_count} geocerca(s)
                          </Text>
                        </View>
                      )}
                      {rule.speed_limit && (
                        <View style={styles.ruleDetailItem}>
                          <Ionicons name="speedometer-outline" size={14} color="#8E8E93" />
                          <Text style={styles.ruleDetailText}>
                            {rule.speed_limit} km/h
                          </Text>
                        </View>
                      )}
                      <View style={styles.ruleDetailItem}>
                        <Ionicons name="time-outline" size={14} color="#8E8E93" />
                        <Text style={styles.ruleDetailText}>
                          Cooldown: {rule.cooldown_minutes} min
                        </Text>
                      </View>
                    </View>

                    {rule.notification_channels.includes('email') && (
                      <View style={styles.recipientsRow}>
                        <Ionicons name="mail-outline" size={14} color="#007AFF" />
                        <Text style={styles.recipientsText}>
                          Email: proprietário da empresa
                        </Text>
                      </View>
                    )}

                    <View style={styles.channelsRow}>
                      {rule.notification_channels.map((channel, idx) => (
                        <View key={idx} style={styles.channelBadge}>
                          <Ionicons 
                            name={channel === 'email' ? 'mail' : channel === 'sms' ? 'chatbubble' : channel === 'push' ? 'notifications' : 'megaphone'} 
                            size={12} 
                            color="#FFFFFF" 
                          />
                          <Text style={styles.channelText}>{channel.toUpperCase()}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                ))
              )}
            </ScrollView>
          )}
        </View>
      </Surface>

      {/* Create/Edit Modal */}
      <Modal
        visible={showCreateModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowCreateModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[
            styles.modalContent,
            { width: Platform.OS === 'web' && width > 768 ? 700 : '95%' }
          ]}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {editingRule ? 'Editar Regra de Alerta' : 'Nova Regra de Alerta'}
              </Text>
              <TouchableOpacity onPress={() => setShowCreateModal(false)}>
                <Ionicons name="close" size={24} color="#000" />
              </TouchableOpacity>
            </View>
            
            <AlertRuleForm
              initialData={editingRule || undefined}
              onSubmit={handleSubmitRule}
              onCancel={() => setShowCreateModal(false)}
              isEditing={!!editingRule}
            />
          </View>
        </View>
      </Modal>

      {/* Mobile Sidebar */}
      {!isLargeScreen && (
        <Sidebar
          key={`mobile-sidebar-${width}`}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={true}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasOwner && hasDriver ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
          onRegisterDriver={!hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  container: {
    flex: 1,
    backgroundColor: '#F2F2F7',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F2F2F7',
  },
  pageHeader: {
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
    gap: 12,
  },
  headerTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#000000',
  },
  createButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#007AFF',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  createButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  switcher: {
    flexDirection: 'row',
    backgroundColor: '#EEF2FF',
    borderRadius: 12,
    padding: 4,
    gap: 4,
    alignSelf: 'flex-start',
  },
  switchButton: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
  },
  switchButtonActive: {
    backgroundColor: '#FFFFFF',
  },
  switchButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6B7280',
  },
  switchButtonTextActive: {
    color: '#111827',
  },
  list: {
    flex: 1,
  },
  listContentContainer: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
    marginTop: 16,
    textAlign: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    color: '#8E8E93',
    marginTop: 8,
    textAlign: 'center',
    paddingHorizontal: 20,
    maxWidth: 500,
  },
  createButtonEmpty: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#007AFF',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 24,
  },
  createButtonEmptyText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  ruleCard: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 16,
    marginTop: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  ruleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  ruleTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  ruleInfo: {
    flex: 1,
  },
  ruleName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
  },
  ruleType: {
    fontSize: 14,
    color: '#8E8E93',
    marginTop: 2,
  },
  inactiveBadge: {
    backgroundColor: '#E5E5EA',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  inactiveBadgeText: {
    fontSize: 12,
    color: '#8E8E93',
  },
  ruleActions: {
    flexDirection: 'row',
    gap: 12,
  },
  actionButton: {
    padding: 4,
  },
  ruleDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 12,
  },
  ruleDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  ruleDetailText: {
    fontSize: 13,
    color: '#8E8E93',
  },
  recipientsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#E5E5EA',
  },
  recipientsText: {
    fontSize: 13,
    color: '#007AFF',
    flex: 1,
  },
  channelsRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  channelBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#007AFF',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  channelText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    maxHeight: '90%',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
      },
      android: {
        elevation: 8,
      },
      web: {
        boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
      } as any,
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#000000',
  },
});
